<script lang="ts">
  import {
    createColumnHelper,
    createSvelteTable,
    getCoreRowModel,
    getExpandedRowModel,
    type Row,
    type Cell,
    type Header,
    type Updater,
    type Column,
    getSortedRowModel,
    type ColumnSort,
    type ColumnDefTemplate,
    type CellContext,
  } from "@tanstack/svelte-table";
  import {
    type GpQueryParams,
    createGPQuery,
    createUnfilteredGPQueryParams,
  } from "../../queries/transactions";
  import {
    createRecordRevenueMutation,
    createRecordSpendMutation,
  } from "../../mutations/transactions";
  import TextNew from "../TextNew.svelte";
  import { derived, get, writable } from "svelte/store";
  import { parseMoneyInputToCents } from "../../helpers/currency";
  import { currencyFormatter } from "../../helpers/localise";
  import { t } from "../../helpers/translations";
  import {
    COL_HEADER_HEIGHT,
    GRID_WIDTH,
    SCROLL_BAR_WIDTH,
    CELL_FADE_BASE_ANIM_CONFIG,
    MAX_TABLE_PAGINATION_CONTROL_HEIGHT,
    GP_CHAT_FILTER_STORAGE_KEY,
    GP_CHAT_FILTER_VERSION,
    CHAT_FILTER_PARAM_KEY,
  } from "./constants";
  import SkeletonColHeader from "./SkeletonColHeader.svelte";
  import InvoiceRowSkeleton from "./InvoiceRowSkeleton.svelte";
  import HeaderCell from "./HeaderCell.svelte";
  import { tableOptions } from "../../helpers/table";
  import { restorePersistedChatFilters } from "../../helpers/gp";
  import { createPersistedLocalStore } from "../../stores/persisted-local-store";
  import { querystring, push, location } from "svelte-spa-router";
  import EmptyStateBasic from "../empty-states/EmptyStateBasic.svelte";
  import ListIcon from "../icons/ListIcon.svelte";
  import { fade } from "svelte/transition";
  import { sideMenuWidth } from "../side-menu/SideMenu.svelte";
  import { __DEV__ } from "../../helpers/constants";
  import { invoicesPageHeaderHeight, invoicesPageOverviewHeight } from "./stores/dimensions";
  import { type SourceInfo, type WeekOverview } from "../../api/transactions";
  import HeaderCellFilter from "./HeaderCellFilter.svelte";
  import { createDropdownMenu } from "../dropdown-menu/create";
  import { onMount } from "svelte";
  import {
    createChatFilterMenuItems,
    syncQueryParamsWithChatFilters,
  } from "../dropdown-menu/chat-filter-menu";
  import type { SupplierChatInfo } from "../../api/suppliers";
  import difference from "lodash/difference";
  import { createSuppliersChatsByBuyerIDsQuery } from "../../queries/suppliers";
  import { selectedBuyer } from "../../stores/buyer";

  const INITIAL_VISIBLE_CHATS = 10;

  let supplierNameColWidth = 0;
  let isChatFilterTooltipVisible = false;
  let hasRestoredFilters = false;

  const supplierFilterBadgeElement = writable<HTMLElement | null>(null);

  type SupplierChatSpendExpandable = {
    type: "chat_spend_expandable";
    year: number;
    week: number;
    expandableChatCount: number;
  };

  type ChatSpendInfo = {
    type: "spend";
    chat_id: string;
    chat_name: string;
    account_number: string | null;
    total_spend: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type ExternalRevenueInfo = {
    type: "external_revenue";
    title: string;
    total_revenue: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type ExternalSpendInfo = {
    type: "external_spend";
    title: string;
    total_spend: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type BuyerRevenueInfo = {
    type: "revenue";
    title: string;
    total_revenue: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type WeekSpendInfo = {
    type: "week";
    title: string;
    total_spend: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type GPInfo = {
    type: "gp";
    title: string;
    profit: number;
    day_mon: number;
    day_tue: number;
    day_wed: number;
    day_thu: number;
    day_fri: number;
    day_sat: number;
    day_sun: number;
    year: number;
    week: number;
  };

  type RowModel =
    | ChatSpendInfo
    | ExternalRevenueInfo
    | ExternalSpendInfo
    | WeekSpendInfo
    | BuyerRevenueInfo
    | GPInfo
    | SupplierChatSpendExpandable;

  type GPViewData = {
    data: Array<RowModel>;
  };

  const columnHelper = createColumnHelper<RowModel>();

  const getDayValue: ColumnDefTemplate<CellContext<RowModel, number>> = (context) => {
    switch (context.row.original.type) {
      case "spend":
      case "revenue":
      case "external_revenue":
      case "external_spend":
      case "week":
        const value = context.getValue<number>();
        if (value === 0) {
          return "—";
        }

        return $currencyFormatter.format(value / 100);
      case "gp":
        return (context.getValue<number>() * 100).toFixed(1) + "%";
    }
  };

  const getTotalValue: ColumnDefTemplate<CellContext<RowModel, number>> = (context) => {
    switch (context.row.original.type) {
      case "spend":
      case "week":
        return $currencyFormatter.format(context.getValue<number>() / 100);
      case "revenue":
      case "external_revenue":
        return $currencyFormatter.format(context.row.original.total_revenue / 100);
      case "external_spend":
        return $currencyFormatter.format(context.row.original.total_spend / 100);
      case "gp":
        return (context.row.original.profit * 100).toFixed(1) + "%";
    }
  };

  const defaultColumns = [
    columnHelper.accessor("chat_name", {
      cell: (context) => {
        if (context.row.original.type === "chat_spend_expandable") {
          return "";
        }
        return "chat_name" in context.row.original
          ? context.row.original.chat_name
          : context.row.original.title;
      },
      header: "supplier",
      id: "chat_name",
      enableSorting: false,
    }),

    columnHelper.accessor("day_mon", {
      cell: getDayValue,
      header: "mon",
      id: "day_mon",
      enableSorting: false,
    }),

    columnHelper.accessor("day_tue", {
      cell: getDayValue,
      header: "tue",
      id: "day_tue",
      enableSorting: false,
    }),

    columnHelper.accessor("day_wed", {
      cell: getDayValue,
      header: "wed",
      id: "day_wed",
      enableSorting: false,
    }),

    columnHelper.accessor("day_thu", {
      cell: getDayValue,
      header: "thu",
      id: "day_thu",
      enableSorting: false,
    }),

    columnHelper.accessor("day_fri", {
      cell: getDayValue,
      header: "fri",
      id: "day_fri",
      enableSorting: false,
    }),

    columnHelper.accessor("day_sat", {
      cell: getDayValue,
      header: "sat",
      id: "day_sat",
      enableSorting: false,
    }),

    columnHelper.accessor("day_sun", {
      cell: getDayValue,
      header: "sun",
      id: "day_sun",
      enableSorting: false,
    }),

    columnHelper.accessor("total_spend", {
      cell: getTotalValue,
      header: "total spend",
      id: "total_spend",
      sortingFn: "alphanumeric",
      enableSorting: true,
    }),
  ];

  const expandedChatListsByWeek = writable<Record<string, boolean>>({});

  const defaultColumnVisibility: Record<string, boolean> = {};
  const skeletonColumns = defaultColumns.filter((c) =>
    !c.id ? false : !(c.id in defaultColumnVisibility)
  );

  const skeletonRows = new Array(9).fill(null);
  const colCount = defaultColumns.length;

  const chatFiltersByID = createPersistedLocalStore<Record<string, boolean>>(
    GP_CHAT_FILTER_STORAGE_KEY,
    {},
    GP_CHAT_FILTER_VERSION
  );

  $: if (hasRestoredFilters && $location === "/gp") {
    syncQueryParamsWithChatFilters($querystring, chatFiltersByID);
  }

  $: isEmptyData = $gpQuery.isSuccess && $gpQuery.data?.weeks?.length === 0;

  const showAllSuppliers = false;

  const unfilteredGpQueryParams = createUnfilteredGPQueryParams();
  const unfilteredGpQuery = createGPQuery(unfilteredGpQueryParams);
  const chatFilterCount = derived(chatFiltersByID, (s) => Object.values(s).filter(Boolean).length);

  const gpQueryParams = derived(
    [unfilteredGpQueryParams, chatFiltersByID, unfilteredGpQuery],
    ([$params, $chatFiltersByID]) => {
      const chatFilterIDs = Object.entries($chatFiltersByID)
        .filter(([_chatID, selected]) => selected)
        .map(([chatID]) => chatID);

      return {
        ...$params,
        selectedChats: chatFilterIDs,
      } satisfies GpQueryParams;
    }
  );

  const gpQuery = createGPQuery(gpQueryParams);

  const recordRevenueMutation = createRecordRevenueMutation();

  function recordRevenue(buyerId: string, date: string, revenue: number) {
    $recordRevenueMutation.mutate(
      { buyer_id: buyerId, date, revenue },
      {
        onSuccess: () => {
          $gpQuery.refetch();
        },
      }
    );
  }

  const recordSpendingMutation = createRecordSpendMutation();

  function recordSpend(buyerId: string, date: string, spend: number) {
    $recordSpendingMutation.mutate(
      { buyer_id: buyerId, date, spend },
      {
        onSuccess: () => {
          $gpQuery.refetch();
        },
      }
    );
  }

  const sorting = writable<ColumnSort[]>([]);

  function setSorting(updater: Updater<ColumnSort[]>) {
    if (updater instanceof Function) {
      sorting.set(updater(get(sorting)));
    } else {
      sorting.set(updater);
    }
  }

  const getTotalsForWeek = (weekInfo: WeekOverview): WeekSpendInfo => {
    const totals = {
      type: "week" as const,
      title: "Total Spend",
      total_spend: weekInfo.total_spend ?? 0,
      day_mon: 0,
      day_tue: 0,
      day_wed: 0,
      day_thu: 0,
      day_fri: 0,
      day_sat: 0,
      day_sun: 0,
      year: weekInfo.spend_summary.year!,
      week: weekInfo.spend_summary.week!,
    };

    const spendSummary = weekInfo.spend_summary;
    if (!spendSummary) {
      return totals;
    }

    spendSummary.days.forEach((day) => {
      switch (day.day) {
        case "Monday":
          totals.day_mon = day.total_spend;
          break;
        case "Tuesday":
          totals.day_tue = day.total_spend;
          break;
        case "Wednesday":
          totals.day_wed = day.total_spend;
          break;
        case "Thursday":
          totals.day_thu = day.total_spend;
          break;
        case "Friday":
          totals.day_fri = day.total_spend;
          break;
        case "Saturday":
          totals.day_sat = day.total_spend;
          break;
        case "Sunday":
          totals.day_sun = day.total_spend;
          break;
        default:
          throw new Error("Invalid day");
      }
    });

    return totals;
  };

  const getExternalRevenueForWeek = (
    weekInfo: WeekOverview,
    sourceList: SourceInfo[]
  ): ExternalRevenueInfo[] => {
    const year = weekInfo.spend_summary.year!;
    const week = weekInfo.spend_summary.week!;

    const sourcesForWeek = weekInfo.sources;
    if (!sourcesForWeek || sourceList.length === 0) {
      return [];
    }

    const sourceTotalRevenues = new Map<string, number>(
      sourcesForWeek.map((s) => [s.source, s.total_revenue])
    );

    const revenuesBySource = new Map<
      string,
      Omit<ExternalRevenueInfo, "title" | "total_revenue" | "year" | "week" | "type">
    >(
      sourceList.map((s) => [
        s.source,
        {
          day_mon: 0,
          day_tue: 0,
          day_wed: 0,
          day_thu: 0,
          day_fri: 0,
          day_sat: 0,
          day_sun: 0,
        },
      ])
    );

    weekInfo.spend_summary.days.forEach((day) => {
      day.external_revenues?.forEach((s) => {
        const sourceRevenue = revenuesBySource.get(s.source);
        if (!sourceRevenue) {
          throw new Error("Supplier spend not found in initial spend map");
        }
        switch (day.day) {
          case "Monday":
            sourceRevenue.day_mon = s.revenue;
            break;
          case "Tuesday":
            sourceRevenue.day_tue = s.revenue;
            break;
          case "Wednesday":
            sourceRevenue.day_wed = s.revenue;
            break;
          case "Thursday":
            sourceRevenue.day_thu = s.revenue;
            break;
          case "Friday":
            sourceRevenue.day_fri = s.revenue;
            break;
          case "Saturday":
            sourceRevenue.day_sat = s.revenue;
            break;
          case "Sunday":
            sourceRevenue.day_sun = s.revenue;
            break;
          default:
            throw new Error("Invalid day");
        }
      });
    });

    const externalRevenues = sourceList.map((s) => {
      return {
        type: "external_revenue" as const,
        title: s.source,
        total_revenue: sourceTotalRevenues.get(s.source) ?? 0,
        year,
        week,
        ...revenuesBySource.get(s.source)!,
      };
    });

    externalRevenues.sort((a, b) => {
      if (a.total_revenue > 0 !== b.total_revenue > 0) {
        return a.total_revenue > 0 ? -1 : 1;
      }
      return a.title.localeCompare(b.title);
    });

    return externalRevenues;
  };

  const getExternalSpendForWeek = (
    weekInfo: WeekOverview,
    sourceList: SourceInfo[]
  ): ExternalSpendInfo[] => {
    const year = weekInfo.spend_summary.year!;
    const week = weekInfo.spend_summary.week!;

    const sourcesForWeek = weekInfo.spend_sources;
    if (!sourcesForWeek || sourceList.length === 0) {
      return [];
    }

    const sourceTotalSpends = new Map<string, number>(
      sourcesForWeek.map((s) => [s.source, s.total_spend])
    );

    const spendsBySource = new Map<
      string,
      Omit<ExternalSpendInfo, "title" | "total_spend" | "year" | "week" | "type">
    >(
      sourceList.map((s) => [
        s.source,
        {
          day_mon: 0,
          day_tue: 0,
          day_wed: 0,
          day_thu: 0,
          day_fri: 0,
          day_sat: 0,
          day_sun: 0,
        },
      ])
    );

    weekInfo.spend_summary.days.forEach((day) => {
      day.external_spends?.forEach((s) => {
        const sourceSpend = spendsBySource.get(s.source);
        if (!sourceSpend) {
          throw new Error("Supplier spend not found in initial spend map");
        }
        switch (day.day) {
          case "Monday":
            sourceSpend.day_mon = s.spend;
            break;
          case "Tuesday":
            sourceSpend.day_tue = s.spend;
            break;
          case "Wednesday":
            sourceSpend.day_wed = s.spend;
            break;
          case "Thursday":
            sourceSpend.day_thu = s.spend;
            break;
          case "Friday":
            sourceSpend.day_fri = s.spend;
            break;
          case "Saturday":
            sourceSpend.day_sat = s.spend;
            break;
          case "Sunday":
            sourceSpend.day_sun = s.spend;
            break;
          default:
            throw new Error("Invalid day");
        }
      });
    });

    const externalSpends = sourceList.map((s) => {
      return {
        type: "external_spend" as const,
        title: s.source,
        total_spend: sourceTotalSpends.get(s.source) ?? 0,
        year,
        week,
        ...spendsBySource.get(s.source)!,
      };
    });

    externalSpends.sort((a, b) => {
      if (a.total_spend > 0 !== b.total_spend > 0) {
        return a.total_spend > 0 ? -1 : 1;
      }
      return a.title.localeCompare(b.title);
    });

    return externalSpends;
  };

  const getRevenueForWeek = (weekInfo: WeekOverview): BuyerRevenueInfo => {
    const revenue = {
      type: "revenue" as const,
      title: "Sales",
      total_revenue: weekInfo.total_manual_revenue ?? 0,
      day_mon: 0,
      day_tue: 0,
      day_wed: 0,
      day_thu: 0,
      day_fri: 0,
      day_sat: 0,
      day_sun: 0,
      year: weekInfo.spend_summary.year!,
      week: weekInfo.spend_summary.week!,
    };

    const spendSummary = weekInfo.spend_summary;
    if (!spendSummary) {
      return revenue;
    }

    spendSummary.days.forEach((day) => {
      switch (day.day) {
        case "Monday":
          revenue.day_mon = day.revenue;
          break;
        case "Tuesday":
          revenue.day_tue = day.revenue;
          break;
        case "Wednesday":
          revenue.day_wed = day.revenue;
          break;
        case "Thursday":
          revenue.day_thu = day.revenue;
          break;
        case "Friday":
          revenue.day_fri = day.revenue;
          break;
        case "Saturday":
          revenue.day_sat = day.revenue;
          break;
        case "Sunday":
          revenue.day_sun = day.revenue;
          break;
        default:
          throw new Error("Invalid day");
      }
    });

    return revenue;
  };

  const getGpForWeek = (weekInfo: WeekOverview): GPInfo => {
    const profit = {
      type: "gp" as const,
      title: t("gp.gpRow.title"),
      profit: weekInfo.profit ?? 0,
      day_mon: 0,
      day_tue: 0,
      day_wed: 0,
      day_thu: 0,
      day_fri: 0,
      day_sat: 0,
      day_sun: 0,
      year: weekInfo.spend_summary.year!,
      week: weekInfo.spend_summary.week!,
    };

    const spendSummary = weekInfo.spend_summary;
    if (!spendSummary) {
      return profit;
    }

    spendSummary.days.forEach((day) => {
      switch (day.day) {
        case "Monday":
          profit.day_mon = day.profit;
          break;
        case "Tuesday":
          profit.day_tue = day.profit;
          break;
        case "Wednesday":
          profit.day_wed = day.profit;
          break;
        case "Thursday":
          profit.day_thu = day.profit;
          break;
        case "Friday":
          profit.day_fri = day.profit;
          break;
        case "Saturday":
          profit.day_sat = day.profit;
          break;
        case "Sunday":
          profit.day_sun = day.profit;
          break;
        default:
          throw new Error("Invalid day");
      }
    });

    return profit;
  };

  const getChatSpendsForWeek = (
    weekInfo: WeekOverview,
    chatList: Pick<ChatSpendInfo, "chat_id">[],
    chatInfo: Map<string, SupplierChatInfo>
  ): ChatSpendInfo[] => {
    const year = weekInfo.spend_summary.year!;
    const week = weekInfo.spend_summary.week!;

    const chatsForWeek = weekInfo.chats;
    if (!chatsForWeek || chatList.length === 0) {
      return [];
    }

    const chatTotalSpends = new Map<string, number>(
      chatsForWeek.map((c) => [c.chat_id, c.total_spend])
    );

    const spendsByChat = new Map<
      string,
      Omit<
        ChatSpendInfo,
        "chat_id" | "chat_name" | "account_number" | "total_spend" | "year" | "week" | "type"
      >
    >(
      chatList.map((s) => [
        s.chat_id,
        {
          day_mon: 0,
          day_tue: 0,
          day_wed: 0,
          day_thu: 0,
          day_fri: 0,
          day_sat: 0,
          day_sun: 0,
        },
      ])
    );

    weekInfo.spend_summary.days.forEach((day) => {
      day.spends.forEach((s) => {
        const chatSpend = spendsByChat.get(s.chat_id);
        if (!chatSpend) {
          throw new Error("Supplier spend not found in initial spend map");
        }
        switch (day.day) {
          case "Monday":
            chatSpend.day_mon = s.spend;
            break;
          case "Tuesday":
            chatSpend.day_tue = s.spend;
            break;
          case "Wednesday":
            chatSpend.day_wed = s.spend;
            break;
          case "Thursday":
            chatSpend.day_thu = s.spend;
            break;
          case "Friday":
            chatSpend.day_fri = s.spend;
            break;
          case "Saturday":
            chatSpend.day_sat = s.spend;
            break;
          case "Sunday":
            chatSpend.day_sun = s.spend;
            break;
          default:
            throw new Error("Invalid day");
        }
      });
    });

    const chatSpends = chatList.map((s) => {
      const info = chatInfo.get(s.chat_id)!;
      return {
        type: "spend" as const,
        chat_id: s.chat_id,
        chat_name: info.name,
        account_number: info.account_number ?? null,
        total_spend: chatTotalSpends.get(s.chat_id) ?? 0,
        year,
        week,
        ...spendsByChat.get(s.chat_id)!,
      };
    });

    chatSpends.sort((a, b) => {
      if (a.total_spend > 0 !== b.total_spend > 0) {
        return a.total_spend > 0 ? -1 : 1;
      }
      return a.chat_name.localeCompare(b.chat_name);
    });

    return chatSpends;
  };

  const gpTableData = derived(
    [gpQuery, expandedChatListsByWeek],
    ([$gpQuery, $expandedChatListsByWeek]): GPViewData => {
      if (!$gpQuery.data) {
        return { data: [] };
      }

      const chats = $gpQuery.data.chats;
      const chatInfo = new Map(chats.map((c) => [c.chat_id, c]));

      const sources = $gpQuery.data.sources ?? [];
      const spendSources = $gpQuery.data.spend_sources ?? [];

      const weeks = $gpQuery.data.weeks;

      return {
        data: weeks.flatMap((weekInfo) => {
          const chatsForWeek = weekInfo.chats;
          const chatList = showAllSuppliers ? chats : chatsForWeek ?? [];

          const viewData: RowModel[] = [];
          let chatSpends = getChatSpendsForWeek(weekInfo, chatList, chatInfo);
          const initialListLength = chatSpends.length;
          const islongList = initialListLength > INITIAL_VISIBLE_CHATS;
          const expandableChatListKey = createExpandableChatListKey(
            weekInfo.spend_summary.week,
            weekInfo.spend_summary.year
          );

          if (islongList && !$expandedChatListsByWeek[expandableChatListKey]) {
            chatSpends = chatSpends.slice(0, INITIAL_VISIBLE_CHATS);
          }

          viewData.push(...chatSpends);

          if (islongList) {
            viewData.push({
              type: "chat_spend_expandable",
              year: weekInfo.spend_summary.year,
              week: weekInfo.spend_summary.week,
              expandableChatCount: initialListLength - INITIAL_VISIBLE_CHATS,
            });
          }

          const totalsForWeek = getTotalsForWeek(weekInfo);
          const externalRevenueForWeek = getExternalRevenueForWeek(weekInfo, sources);
          const externalSpendForWeek = getExternalSpendForWeek(weekInfo, spendSources);
          const revenueForWeek = getRevenueForWeek(weekInfo);
          const gpForWeek = getGpForWeek(weekInfo);

          return [
            ...viewData,
            ...externalSpendForWeek,
            totalsForWeek,
            ...externalRevenueForWeek,
            revenueForWeek,
            gpForWeek,
          ];
        }),
      };
    }
  );

  function createExpandableChatListKey(week: number, year: number) {
    return `${week}${year}`;
  }

  function shouldShowDate(row: Row<RowModel>, index: number, rows: Row<RowModel>[]) {
    if (index === 0) {
      return true;
    }
    const lastRow = rows[index - 1];
    if (!lastRow) {
      return false;
    }
    return lastRow.original.week !== row.original.week;
  }

  const throttledUpdateHorizontalScrollShadow = rafThrottle(updateHorizontalScrollShadow);

  function handleGridScroll(
    e: UIEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    }
  ) {
    throttledUpdateHorizontalScrollShadow(e.currentTarget.scrollLeft);
  }

  function updateHorizontalScrollShadow(scrollLeft: number) {
    if (scrollLeft > 0) {
      scrollGradientWidth = "40px";
    } else {
      scrollGradientWidth = "0px";
    }
  }

  function rafThrottle(cb: (...args: any[]) => void) {
    let requestID: number;

    return (...args: any[]) => {
      cancelAnimationFrame(requestID);
      requestID = requestAnimationFrame(() => {
        cb(...args);
      });
    };
  }

  const getRowId = (row: RowModel) => {
    let optionalSuffix = "";
    if ("chat_id" in row) {
      optionalSuffix = row.chat_id;
    } else if (row.type === "external_revenue") {
      optionalSuffix = row.title;
    }
    return `${row.year}/${row.week}/${row.type}/${optionalSuffix}`;
  };

  const table = createSvelteTable(
    derived([gpTableData, sorting], ([$gpTableData, $sorting]) =>
      tableOptions({
        data: $gpTableData.data,
        columns: defaultColumns,
        state: {
          sorting: $sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: getRowId,
        debugColumns: __DEV__,
        debugTable: __DEV__,
        debugHeaders: __DEV__,
        enableSortingRemoval: false,
      })
    )
  );

  $: rows = $table.getRowModel().rows;

  $: scrollGradientWidth = "0px";

  const columnWidthsByID = new Map<keyof (ChatSpendInfo & BuyerRevenueInfo & GPInfo), string>([
    ["chat_name", "minmax(max-content, 200px)"],
    ["day_mon", "minmax(max-content, 100px)"],
    ["day_tue", "minmax(max-content, 100px)"],
    ["day_wed", "minmax(max-content, 100px)"],
    ["day_thu", "minmax(max-content, 100px)"],
    ["day_fri", "minmax(max-content, 100px)"],
    ["day_sat", "minmax(max-content, 100px)"],
    ["day_sun", "minmax(max-content, 100px)"],
    ["total_spend", "minmax(max-content, 200px)"],
    ["total_revenue", "minmax(max-content, 200px)"],
    ["profit", "minmax(max-content, 200px)"],
  ]);

  const {
    open: chatFilterOpen,
    menu: chatFilterMenu,
    menuAttrs: chatFilterMenuAttrs,
    triggerAttrs: chatFilterTriggerAttrs,
    onTriggerClick: chatFilterOnTriggerClick,
    triggerElement: chatFilterTriggerElement,
  } = createDropdownMenu();

  $: visibleColumnWidths = getVisibleColumnWidths(
    $table.getAllLeafColumns(),
    $gpQuery.isLoading || $unfilteredGpQuery.isLoading
  );

  function getVisibleColumnWidths(columns: Column<RowModel, unknown>[], isLoading: boolean) {
    const colWidths: string[] = [];

    if (isLoading) {
      skeletonColumns.forEach((c) => {
        if (c.id) {
          colWidths.push(columnWidthsByID.get(c.id) ?? "1fr");
        }
      });
    } else {
      columns.forEach((c) => {
        if (c.getIsVisible()) {
          colWidths.push(columnWidthsByID.get(c.id) ?? "1fr");
        }
      });
    }

    return colWidths.join(" ");
  }

  const supplierChatsQueryParams = derived(
    [selectedBuyer, chatFiltersByID, unfilteredGpQuery],
    ([$selectedBuyer, $chatFiltersByID, $unfilteredGpQuery]) => {
      return {
        buyerIDs: [$selectedBuyer?.id ?? ""],
        chatIDs: difference(
          Object.keys($chatFiltersByID),
          $unfilteredGpQuery.data?.chats?.map((c) => c.chat_id) ?? []
        ),
      };
    }
  );
  const supplierChatsUnavailableForDateRange =
    createSuppliersChatsByBuyerIDsQuery(supplierChatsQueryParams);

  $: chatFilterMenuItems = createChatFilterMenuItems(
    $unfilteredGpQuery.data?.chats,
    $chatFiltersByID,
    $supplierChatsUnavailableForDateRange.data
  );

  function getDateRangeOfWeek(year: number, week: number) {
    const firstJan = new Date(year, 0, 1);
    const daysToAdd = (1 - firstJan.getDay() + 7) % 7; // Days to next Monday
    const firstMondayOfYear = new Date(firstJan.getFullYear(), 0, 1 + daysToAdd);

    const firstThursdayOfYear = new Date(firstMondayOfYear);
    firstThursdayOfYear.setDate(firstMondayOfYear.getDate() + 3);

    const weekMillisecondOffset = (week - 1) * 7 * 24 * 60 * 60 * 1000;
    const weekStartDate = new Date(firstThursdayOfYear.getTime() + weekMillisecondOffset);
    weekStartDate.setDate(weekStartDate.getDate() - 3);

    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekStartDate.getDate() + 6);

    const options = { month: "long", day: "numeric" } satisfies Intl.DateTimeFormatOptions;
    const startFormat = new Intl.DateTimeFormat("en-US", options).format(weekStartDate);
    const endFormat = new Intl.DateTimeFormat("en-US", options).format(weekEndDate);

    return `${startFormat} – ${endFormat}`;
  }

  function getDateOfISOWeek(year: number, week: number, day: number) {
    const firstJan = new Date(year, 0, 1);
    const daysToAdd = (1 - firstJan.getDay() + 7) % 7;
    const firstMondayOfYear = new Date(year, 0, 1 + daysToAdd);

    const firstThursdayOfYear = new Date(firstMondayOfYear);
    firstThursdayOfYear.setDate(firstMondayOfYear.getDate() + 3);

    const weekMillisecondOffset = (week - 1) * 7 * 24 * 60 * 60 * 1000;
    const weekStartDate = new Date(firstThursdayOfYear.getTime() + weekMillisecondOffset);
    weekStartDate.setDate(weekStartDate.getDate() - 3 + day);

    const yearStr = weekStartDate.getFullYear();
    const monthStr = (weekStartDate.getMonth() + 1).toString().padStart(2, "0");
    const dayStr = weekStartDate.getDate().toString().padStart(2, "0");

    return `${yearStr}-${monthStr}-${dayStr}`;
  }

  const dayToIndexMap = {
    mon: 0,
    tue: 1,
    wed: 2,
    thu: 3,
    fri: 4,
    sat: 5,
    sun: 6,
  };

  function cellIdToDay(cellId: string) {
    return dayToIndexMap[cellId.split("/")[3].split("_")[2] as keyof typeof dayToIndexMap];
  }

  function getMonthYearDivider(row: Row<RowModel>, index: number, rows: Row<RowModel>[]) {
    const isFirstRow = index === 0;
    const isDiffDate = isFirstRow ? false : shouldShowDate(row, index, rows);

    return isFirstRow || isDiffDate
      ? getDateRangeOfWeek(row.original.year, row.original.week)
      : null;
  }

  function renderCellValue(cell: Cell<RowModel, unknown>): string | null {
    if (typeof cell.column.columnDef.cell === "function") {
      return cell.column.columnDef.cell?.(cell.getContext());
    }
    return null;
  }

  function renderHeaderValue(header: Header<RowModel, unknown>): string | null {
    if (typeof header.column.columnDef.header === "function") {
      return header.column.columnDef.header(header.getContext());
    }
    if (typeof header.column.columnDef.header === "string") {
      return header.column.columnDef.header;
    }
    return null;
  }

  function renderRevenueCell(cell: Cell<RowModel, unknown>) {
    const value = cell.getValue<number>();
    if (!value) return "";
    return (value / 100).toFixed(2);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== "K") return;
    e.stopPropagation();
    const queryParams = new URLSearchParams($querystring);
    if (queryParams.has("exp")) {
      queryParams.delete("exp");
    } else {
      queryParams.append("exp", "true");
    }
    const newQueryString = queryParams.toString();
    const newUrl = `${$location}?${newQueryString}`;
    push(newUrl);
  }

  onMount(() => {
    restorePersistedChatFilters(chatFiltersByID);
    hasRestoredFilters = true;
  });
</script>

<svelte:window on:keydown={onKeyDown} />

<div
  style:position="relative"
  style:--grid-width={GRID_WIDTH}
  style:--col-header-height={COL_HEADER_HEIGHT}
  style:--page-header-height="{$invoicesPageHeaderHeight}px"
  style:--overview-height="{$invoicesPageOverviewHeight}px"
  style:--scrollbar-width={SCROLL_BAR_WIDTH}
  style:--side-menu-width="{$sideMenuWidth}px"
  style:--pagination-control-height="{MAX_TABLE_PAGINATION_CONTROL_HEIGHT}px"
  style:height="100%"
>
  {#if $gpQuery.isPlaceholderData}
    <div class="shimmer-loader" />
  {/if}

  <div
    class="main-grid"
    role="grid"
    aria-colcount={colCount}
    style:grid-template-columns={visibleColumnWidths}
    style:--scroll-gradient-width={scrollGradientWidth}
    on:scroll={handleGridScroll}
  >
    {#each $table.getHeaderGroups() as headerGroup}
      <div class="header-row">
        {#if $gpQuery.isLoading || $unfilteredGpQuery.isLoading}
          {#each skeletonColumns as col (col.id)}
            <SkeletonColHeader colID={col.id ?? ""} end={col.id !== "chat_name"} />
          {/each}
        {:else}
          {#each headerGroup.headers as header (header.id)}
            {#if header.id === "total_spend"}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCell
                edge="end"
                gradientFade="start"
                --sticky-top="0"
                --sticky-right="0"
                --z-index="3"
                --justify="flex-end"
              >
                <TextNew
                  font="ocr"
                  size="m"
                  --rk-text-color="var(--rk-text-transparency-dark)"
                  --whitespace="nowrap"
                >
                  {headerValue}
                </TextNew>
              </HeaderCell>
            {:else if header.id === "chat_name"}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCellFilter
                edge="start"
                zIndex={4}
                on:click={chatFilterOnTriggerClick}
                bind:triggerElement={$chatFilterTriggerElement}
                bind:colWidth={supplierNameColWidth}
                filterQueryParam={CHAT_FILTER_PARAM_KEY}
                badgeElement={supplierFilterBadgeElement}
                filterCount={$chatFilterCount}
                isHintTooltipVisible={isChatFilterTooltipVisible}
                filterMenuAction={chatFilterMenu}
                menuAttrs={$chatFilterMenuAttrs}
                triggerAttrs={$chatFilterTriggerAttrs}
                isFilterMenuOpen={$chatFilterOpen}
                filterMenuItems={chatFilterMenuItems}
                stickyLeft={0}
                shadow
              >
                {headerValue}
              </HeaderCellFilter>
            {:else}
              {@const headerValue = renderHeaderValue(header)}
              <HeaderCell
                --sticky-top="0"
                --z-index={3}
                --justify="flex-end"
                sortable={header.column.getCanSort()}
                sortDirection={header.column.getIsSorted()}
                on:click={header.column.getToggleSortingHandler()}
              >
                <TextNew
                  font="ocr"
                  size="m"
                  --rk-text-color="var(--rk-text-transparency-dark)"
                  --whitespace="nowrap"
                >
                  {headerValue}
                </TextNew>
              </HeaderCell>
            {/if}
          {/each}
        {/if}
      </div>
    {/each}

    <div class="body-grid" class:body-gap={$gpQuery.isLoading || $unfilteredGpQuery.isLoading}>
      {#if $gpQuery.isLoading || $unfilteredGpQuery.isLoading}
        {#each skeletonRows as _}
          <InvoiceRowSkeleton hideDateCell columns={7} />
        {/each}
      {:else if !isEmptyData}
        {#each rows as row, index (row.id)}
          {@const isParent = !Boolean(row.parentId)}
          {@const monthYearDivider = getMonthYearDivider(row, index, rows)}
          {@const shouldShowDivider =
            index !== rows.length - 1 &&
            rows[index + 1]?.original.type !== "week" &&
            row.original.type !== "gp"}
          {@const isCalculationsBoxRow =
            row.original.type !== "spend" && row.original.type !== "external_spend"}
          {@const cellBackground = isCalculationsBoxRow ? "var(--rk-neutral-0)" : "#fff"}
          {#if !!monthYearDivider}
            <div class="row">
              <div
                class="date-panel"
                style:--shadow-index={index - rows.length}
                in:fade|global={CELL_FADE_BASE_ANIM_CONFIG}
                out:fade={CELL_FADE_BASE_ANIM_CONFIG}
              >
                <TextNew weight="medium" size="s">
                  {monthYearDivider}
                </TextNew>
              </div>
              <div class="date-panel-row-stub" />
            </div>
            <div class="row-divider-wrapper divider-padding date-panel-divider-sticky-top">
              <div class="row-divider" />
            </div>
          {/if}
          {#if row.original.type === "chat_spend_expandable"}
            {@const expandableChatListKey = createExpandableChatListKey(
              row.original.week,
              row.original.year
            )}
            <div class="list-expandable-row" role="row" tabindex={0} data-type={row.original.type}>
              <button
                class="list-expandable-button"
                on:click={() => {
                  expandedChatListsByWeek.update((expanded) => {
                    return {
                      ...expanded,
                      [expandableChatListKey]: !expanded[expandableChatListKey],
                    };
                  });
                }}
              >
                <TextNew
                  as="span"
                  size="xs"
                  --size="var(--rk-font-size-ocr-m)"
                  --rk-text-color="var(--rk-text-low)"
                >
                  {$expandedChatListsByWeek[expandableChatListKey]
                    ? "collapse suppliers"
                    : `show ${row.original.expandableChatCount} more suppliers`}
                </TextNew>
              </button>
            </div>
          {:else}
            <div class="row" role="row" tabindex={0} data-type={row.original.type}>
              {#each row.getVisibleCells() as cell (cell.id)}
                {@const cellValue = renderCellValue(cell)}
                {@const isEmphasizedText =
                  "title" in row.original &&
                  (cell.column.id === "chat_name" || cell.column.id === "total_spend")}

                <div
                  role="gridcell"
                  in:fade|global={CELL_FADE_BASE_ANIM_CONFIG}
                  out:fade={CELL_FADE_BASE_ANIM_CONFIG}
                  class="cell-gap"
                  class:input-cell={cell.column.id.startsWith("day_")}
                  class:chat-cell-sticky={cell.column.id === "chat_name"}
                  style:left={cell.column.id === "chat_name" ? "0px" : undefined}
                  class:total-spend-cell={cell.column.id === "total_spend"}
                  style:background={cellBackground}
                >
                  {#if cell.column.id.startsWith("day_") && ((cell.row.original.type === "revenue" && cell.row.id.includes("revenue")) || cell.row.original.type === "external_spend")}
                    <div>
                      <input
                        on:change={(e) => {
                          const date = e.currentTarget.dataset.date;
                          if (!date) throw new Error("Invalid price cell");

                          if (!e.currentTarget.checkValidity()) {
                            console.warn("Invalid value for revenue input", {
                              date,
                              value: e.currentTarget.value,
                            });
                            e.currentTarget.value = "0";
                            return;
                          }

                          const revenueOrSpend = parseMoneyInputToCents(e.currentTarget.value);
                          const buyerId = $selectedBuyer?.id;
                          if (!buyerId) {
                            throw new Error("No buyer selected");
                          }

                          if (cell.row.original.type === "external_spend") {
                            recordSpend(buyerId, date, Math.floor(revenueOrSpend));
                          } else {
                            recordRevenue(buyerId, date, Math.floor(revenueOrSpend));
                          }
                        }}
                        data-initial={cell.getValue()}
                        data-date={getDateOfISOWeek(
                          row.original.year,
                          row.original.week,
                          cellIdToDay(cell.id)
                        )}
                        type="text"
                        value={renderRevenueCell(cell)}
                        placeholder="0.00"
                        class="revenue-input"
                        style="width: 100%;"
                      />
                    </div>
                  {:else if isEmphasizedText}
                    <div>
                      <TextNew size="xs" weight="medium" --size="var(--rk-font-size-ocr-m)">
                        {cellValue}
                      </TextNew>
                    </div>
                  {:else if (isParent || cell.column.id !== "chat_name") && cellValue != null}
                    <div>
                      <TextNew
                        size="xs"
                        --rk-text-color="var(--rk-text-dark)"
                        --overflow={cell.column.id === "notes" ? "hidden" : undefined}
                        --size="var(--rk-font-size-ocr-m)"
                        overflow={cell.column.id === "notes" ? "lineClamp" : "initial"}
                      >
                        {cellValue}

                        {#if row.original.type === "spend" && row.original.account_number && cell.column.id === "chat_name"}
                          <TextNew
                            size="xs"
                            --rk-text-color="var(--rk-text-low)"
                            --overflow="hidden"
                            --size="var(--rk-font-size-ocr-m)"
                          >
                            {row.original.account_number}
                          </TextNew>
                        {/if}
                      </TextNew>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}

          {#if shouldShowDivider}
            <div class="row-divider-wrapper divider-padding" style:background={cellBackground}>
              <div class="row-divider" />
            </div>
          {/if}
        {/each}
      {/if}
    </div>
  </div>

  {#if isEmptyData}
    <div class="empty-state">
      <EmptyStateBasic
        title="no data for this period"
        subtitle="edit filters or try another date range"
      >
        <svelte:component this={ListIcon} slot="illustration" />
      </EmptyStateBasic>
    </div>
  {/if}
</div>

<style>
  .main-grid {
    display: grid;
    margin-top: var(--page-vertical-gap);
    overflow: auto;
    max-width: calc(100vw - var(--side-menu-width) - var(--content-px) * 2);
    max-height: calc(
      100vh - var(--page-header-height) - (var(--page-vertical-gap) * 3) - var(--scrollbar-width) -
        (var(--page-vertical-padding) * 2) + 50px
    );
  }

  .list-expandable-row {
    grid-column: 1 / -1;
  }

  .list-expandable-button {
    display: block;
    padding: 16px;
  }

  .chat-cell-sticky {
    position: sticky;
    background-color: white;
    z-index: 1;
  }

  .date-panel::after,
  .chat-cell-sticky::after {
    content: "";
    position: absolute;
    top: 0;
    left: 100%;
    bottom: 0;
    z-index: -1;
    display: block;
    width: var(--scroll-gradient-width);
    will-change: width;
    background: linear-gradient(
      270deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.01) 38.87%,
      rgba(0, 0, 0, 0.02) 60.71%,
      rgba(0, 0, 0, 0.03) 75.41%,
      rgba(0, 0, 0, 0.03) 90.08%,
      rgba(0, 0, 0, 0.04) 97.27%,
      rgba(0, 0, 0, 0.06) 99.46%,
      rgba(0, 0, 0, 0.11) 100%
    );
    transition: width 0.2s ease-in-out;
  }

  .date-panel::before {
    content: "";
    position: absolute;
    top: 0;
    left: 100%;
    bottom: 0;
    display: block;
    width: var(--scroll-gradient-width);
    z-index: -1;
    background: var(--rekki-light);
  }

  .body-grid {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  }

  .body-gap {
    margin-top: 8px;
  }

  .header-row {
    display: contents;
  }

  .empty-state {
    width: 100%;
    height: 80vh;
    padding-bottom: 32px;
  }

  .total-spend-cell {
    justify-content: flex-end;
  }

  .date-panel-divider-sticky-top {
    position: sticky;
    top: calc(58px + var(--col-header-height));
    z-index: 2;
  }

  .date-panel {
    padding: 21px 16px 13px 16px;
    text-transform: none;
    grid-column: 1;
    position: sticky;
    top: var(--col-header-height);
    background: var(--rekki-light);
    left: 0px;
    z-index: 3;
  }

  .date-panel-row-stub {
    position: sticky;
    top: var(--col-header-height);
    grid-column: 2 / span lastline;
    background: var(--rekki-light);
    z-index: 2;
  }

  .row {
    display: contents;
  }

  .row-divider-wrapper {
    grid-column: 1 / -1;
  }

  .row-divider {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-color: var(--rk-divider-0);
  }

  .divider-padding {
    padding-left: 16px;
    padding-right: 16px;
  }

  input.revenue-input {
    font-family: "DiatypeRekki";
    /* font-variant-numeric: normal; */
    font-size: var(--rk-font-size-ocr-m);
    text-align: right;
    line-height: var(--rk-line-height-xs);

    position: relative;
    left: 12px;
    letter-spacing: -0.005em;

    background: var(--rk-primary-light);
    max-width: 102px;
    border: 1px solid var(--rk-divider-0);
    border-radius: var(--rk-rounding-s);
    padding: 12px;
  }

  div[role="gridcell"] {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 64px;
  }

  :first-of-type[role="gridcell"] {
    justify-content: flex-start;
  }

  .row[data-type="gp"] :first-of-type[role="gridcell"],
  .row[data-type="week"] :first-of-type[role="gridcell"],
  .row[data-type="external_revenue"] :first-of-type[role="gridcell"],
  .row[data-type="revenue"] :first-of-type[role="gridcell"] {
    border-left: 1px solid var(--rk-divider-0);
  }

  .row[data-type="gp"] :last-of-type[role="gridcell"],
  .row[data-type="week"] :last-of-type[role="gridcell"],
  .row[data-type="external_revenue"] :last-of-type[role="gridcell"],
  .row[data-type="revenue"] :last-of-type[role="gridcell"] {
    border-right: 1px solid var(--rk-divider-0);
  }

  .row[data-type="week"] :first-of-type[role="gridcell"] {
    border-top-left-radius: var(--rk-rounding-s);
  }
  .row[data-type="week"] :last-of-type[role="gridcell"] {
    border-top-right-radius: var(--rk-rounding-s);
  }
  .row[data-type="week"] [role="gridcell"] {
    border-top: 1px solid var(--rk-divider-0);
  }

  .row[data-type="gp"] :first-of-type[role="gridcell"] {
    border-bottom-left-radius: var(--rk-rounding-s);
  }
  .row[data-type="gp"] :last-of-type[role="gridcell"] {
    border-bottom-right-radius: var(--rk-rounding-s);
  }
  .row[data-type="gp"] [role="gridcell"] {
    border-bottom: 1px solid var(--rk-divider-0);
  }

  .cell-gap {
    padding-left: 16px;
    padding-right: 16px;
  }

  .input-cell {
    padding-left: 8px;
    padding-right: 0px;
  }

  .shimmer-loader {
    position: absolute;
    top: var(--col-header-height);
    width: calc(var(--grid-width) - var(--scrollbar-width));
    bottom: calc(40px + var(--scrollbar-width));
    left: 0;
    overflow-x: hidden;
    z-index: 3;
    pointer-events: none;
  }

  .shimmer-loader::after {
    content: "";
    position: absolute;
    top: 0;
    right: -100%;
    bottom: 0;
    left: -100%;
    background-image: linear-gradient(
      to right,
      rgb(255 255 255 / 0) 33.3%,
      rgb(255 255 255 / 0.8),
      rgb(255 255 255 / 0) 66.6%
    );
    animation: shimmer 1.2s infinite cubic-bezier(0.42, 0, 0.5, 1);
  }

  @keyframes shimmer {
    from {
      transform: translateX(-33.3%);
    }
    to {
      transform: translateX(33.3%);
    }
  }
</style>
